import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useId, useRef, useState } from "react";
import { ArrowLeft, Loader2, Mail, Phone } from "lucide-react";

import { Header } from "@/components/Header";
import logo from "@/assets/logo.svg";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SegmentedOtpInput } from "@/components/ui/segmented-otp-input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useAuth as useFirebaseAuth, type PhoneSignInChallenge } from "@/lib/firebase/auth";
import { navigateAfterAuth, usePostAuthRedirectParam } from "@/lib/post-auth-redirect";
import { toastError } from "@/lib/toast";

const getLocationContext = () => {
  if (typeof window === "undefined") return {};
  return {
    hostname: window.location.hostname,
    origin: window.location.origin,
  };
};

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Sign in — Real Pranks" },
      {
        name: "description",
        content:
          "Sign in to Real Pranks with email, Google, Apple, or your phone number.",
      },
      { property: "og:title", content: "Sign in — Real Pranks" },
      {
        property: "og:description",
        content: "Access your Real Pranks account.",
      },
    ],
  }),
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const redirect = usePostAuthRedirectParam();
  const {
    signIn,
    signInWithGoogle,
    signInWithApple,
    startPhoneSignIn,
    pendingAccountMerge,
    completeDeferredMerge,
    clearAccountMerge,
  } = useAuth();
  const { signedIn: firebaseSignedIn } = useFirebaseAuth();

  // Email/password
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);

  // Social
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [mergeNow, setMergeNow] = useState(false);
  const [mergeLoading, setMergeLoading] = useState(false);

  // Phone
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [challenge, setChallenge] = useState<PhoneSignInChallenge | null>(null);
  const recaptchaContainerId = useId();
  const recaptchaIdRef = useRef(`recaptcha-${recaptchaContainerId.replace(/[:]/g, "")}`);
  const [authTab, setAuthTab] = useState<"email" | "phone">("email");

  // If already signed in, bounce away (use real Firebase auth, not dev override).
  useEffect(() => {
    if (firebaseSignedIn) {
      navigateAfterAuth(navigate, redirect);
    }
  }, [firebaseSignedIn, navigate, redirect]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailLoading(true);
    const result = await signIn({ email, password });
    setEmailLoading(false);
    if ("error" in result) {
      toastError(result.error, { flow: "email-sign-in" });
      return;
    }
    if (mergeNow && pendingAccountMerge) {
      setMergeLoading(true);
      const mergeResult = await completeDeferredMerge();
      setMergeLoading(false);
      if ("error" in mergeResult) {
        toastError(mergeResult.error, { flow: "deferred-merge" });
        return;
      }
      toast.success(mergeResult.message ?? "Accounts merged.");
    }
    toast.success("Signed in.");
    navigateAfterAuth(navigate, redirect);
  };

  const handleGoogle = async () => {
    setGoogleLoading(true);
    const result = await signInWithGoogle();
    setGoogleLoading(false);
    if ("error" in result) {
      if (result.mergeRequired) {
        setAuthTab("email");
      }
      toastError(result.error, { flow: "google-sign-in" });
      return;
    }
    toast.success("Signed in with Google.");
    navigateAfterAuth(navigate, redirect);
  };

  const handleApple = async () => {
    setAppleLoading(true);
    const result = await signInWithApple();
    setAppleLoading(false);
    if ("error" in result) {
      if (result.mergeRequired) {
        setAuthTab("email");
      }
      toastError(result.error, { flow: "apple-sign-in" });
      return;
    }
    toast.success("Signed in with Apple.");
    navigateAfterAuth(navigate, redirect);
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\+\d{6,}$/.test(phone)) {
      toastError("Enter your phone number in international format (e.g. +15551234567).", {
        flow: "phone-sign-in-validate",
      });
      return;
    }
    setPhoneLoading(true);
    const result = await startPhoneSignIn(phone, recaptchaIdRef.current);
    setPhoneLoading(false);
    if ("error" in result) {
      toastError(result.error, {
        flow: "phone-send-code",
        ...getLocationContext(),
      });
      return;
    }
    setChallenge(result.challenge);
    toast.success("Code sent. Check your messages.");
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!challenge) return;
    if (!/^\d{4,8}$/.test(code)) {
      toastError("Enter the verification code.", { flow: "phone-verify-validate" });
      return;
    }
    setPhoneLoading(true);
    const result = await challenge.confirm(code);
    setPhoneLoading(false);
    if ("error" in result) {
      toastError(result.error, {
        flow: "phone-verify",
        ...getLocationContext(),
      });
      return;
    }
    toast.success("Signed in.");
    navigateAfterAuth(navigate, redirect);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="mx-auto flex w-full max-w-[92%] flex-col gap-4 px-4 py-8 sm:max-w-md">
        <button
          type="button"
          onClick={() => {
            if (typeof window !== "undefined" && window.history.length > 1) {
              window.history.back();
            } else {
              navigate({ to: "/" });
            }
          }}
          className="inline-flex items-center gap-1.5 self-start text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <img src={logo} alt="" className="mx-auto h-16 w-auto pt-4" />
        <h1 className="text-center text-[1.4rem] font-bold leading-tight">Log In</h1>

        <div className="flex flex-col gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleGoogle}
            disabled={googleLoading || appleLoading}
            className="h-11 gap-3 border-[#dadce0] bg-white text-[14px] font-medium text-[#1f1f1f] hover:bg-[#f8f9fa] hover:text-[#1f1f1f] dark:border-border dark:bg-background dark:text-foreground dark:hover:bg-accent dark:hover:text-foreground"
          >
            {googleLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <GoogleIcon className="h-5 w-5 shrink-0" />
            )}
            Sign in with Google
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleApple}
            disabled={googleLoading || appleLoading}
            className="h-11 gap-3 border-border bg-background text-[14px] font-medium hover:bg-accent"
          >
            {appleLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <AppleIcon className="h-5 w-5 shrink-0" />
            )}
            Sign in with Apple
          </Button>
        </div>
        {pendingAccountMerge && (
          <div className="rounded-lg border border-amber-300/50 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
            <p className="font-medium">Account merge available</p>
            <p className="mt-1 text-xs">
              {pendingAccountMerge.email} already has an account. You can merge {pendingAccountMerge.providerId === "apple.com" ? "Apple" : "Google"} after signing in with your existing method.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant={mergeNow ? "default" : "outline"}
                onClick={() => setMergeNow((v) => !v)}
                disabled={mergeLoading}
              >
                {mergeNow ? "Merge after sign-in enabled" : "Merge now after email sign-in"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setMergeNow(false);
                  clearAccountMerge();
                }}
              >
                Not now
              </Button>
            </div>
          </div>
        )}

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">or</span>
          </div>
        </div>

        <Tabs value={authTab} onValueChange={(value) => setAuthTab(value as "email" | "phone")} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="email">
              <Mail className="mr-2 h-4 w-4" />
              Email
            </TabsTrigger>
            <TabsTrigger value="phone">
              <Phone className="mr-2 h-4 w-4" />
              Phone
            </TabsTrigger>
          </TabsList>

          <TabsContent value="email" className="mt-4">
            <form onSubmit={handleEmailSubmit} className="flex flex-col gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                </div>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <Button type="submit" disabled={emailLoading} className="h-11">
                {emailLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Sign in
              </Button>
              <ResetPasswordRow defaultEmail={email} />
            </form>
          </TabsContent>

          <TabsContent value="phone" className="mt-4">
            {challenge ? (
              <form onSubmit={handleVerifyCode} className="flex flex-col gap-6">
                <div className="rounded-2xl border border-border bg-card p-6 text-card-foreground shadow-md">
                  <p className="text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Check your messages
                  </p>
                  <h2 className="mt-2 text-center text-2xl font-bold tracking-tight">
                    Enter verification code
                  </h2>
                  <p className="mt-2 text-center text-sm text-muted-foreground">
                    We sent a code to{" "}
                    <span className="font-medium text-foreground">{phone}</span>
                  </p>
                  <div className="mt-6">
                    <Label htmlFor="code" className="sr-only">
                      Verification code
                    </Label>
                    <SegmentedOtpInput
                      id="code"
                      value={code}
                      onChange={setCode}
                      length={8}
                      disabled={phoneLoading}
                      autoFocus
                      aria-label="SMS verification code"
                    />
                    <p className="mt-3 text-center text-xs text-muted-foreground">
                      You can type each digit or paste the full code.{" "}
                      <button
                        type="button"
                        className="font-medium text-foreground underline-offset-4 hover:underline"
                        onClick={() => {
                          setChallenge(null);
                          setCode("");
                        }}
                      >
                        Use a different number
                      </button>
                    </p>
                  </div>
                </div>
                <Button type="submit" disabled={phoneLoading} className="h-11">
                  {phoneLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Verify and sign in
                </Button>
              </form>
            ) : (
              <form onSubmit={handleSendCode} className="flex flex-col gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    autoComplete="tel"
                    placeholder="+15551234567"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Include your country code, e.g. +1 for the US.
                  </p>
                </div>
                <Button type="submit" disabled={phoneLoading} className="h-11">
                  {phoneLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Send code
                </Button>
              </form>
            )}
            <div id={recaptchaIdRef.current} className="mt-2" />
          </TabsContent>
        </Tabs>

        <div className="flex justify-center pb-8 pt-4">
          <Button variant="ghost" className="h-auto min-w-[50%] text-foreground" asChild>
            <Link
              to="/signup"
              className="text-center font-medium"
              {...(redirect ? { search: { redirect } } : {})}
            >
              Create an account?
            </Link>
          </Button>
        </div>
      </main>
    </div>
  );
}

function ResetPasswordRow({ defaultEmail }: { defaultEmail: string }) {
  const { sendPasswordReset } = useAuth();
  const [sending, setSending] = useState(false);

  const handleClick = async () => {
    if (!defaultEmail) {
      toastError("Enter your email above first, then tap reset password.", {
        flow: "reset-password-validate",
      });
      return;
    }
    setSending(true);
    const result = await sendPasswordReset(defaultEmail);
    setSending(false);
    if ("error" in result) {
      toastError(result.error, { flow: "reset-password" });
      return;
    }
    toast.success(result.message ?? "Reset email sent.");
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={sending}
      className="text-center text-xs text-muted-foreground hover:text-foreground disabled:opacity-60"
    >
      {sending ? "Sending reset email…" : "Forgot your password? Send a reset email"}
    </button>
  );
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        fill="#4285F4"
        d="M23.49 12.27c0-.79-.07-1.54-.2-2.27H12v4.29h6.47a5.53 5.53 0 0 1-2.4 3.63v3h3.88c2.27-2.09 3.54-5.16 3.54-8.65Z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.96-1.07 7.95-2.91l-3.88-3c-1.08.73-2.45 1.17-4.07 1.17-3.13 0-5.78-2.11-6.73-4.96H1.27v3.09A12 12 0 0 0 12 24Z"
      />
      <path
        fill="#FBBC05"
        d="M5.27 14.3A7.2 7.2 0 0 1 4.9 12c0-.8.14-1.58.37-2.3V6.61H1.27A12 12 0 0 0 0 12c0 1.94.46 3.77 1.27 5.39l4-3.09Z"
      />
      <path
        fill="#EA4335"
        d="M12 4.74c1.76 0 3.35.61 4.59 1.8l3.44-3.44C17.95 1.13 15.24 0 12 0A12 12 0 0 0 1.27 6.61l4 3.09c.95-2.85 3.6-4.96 6.73-4.96Z"
      />
    </svg>
  );
}

function AppleIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      fill="currentColor"
    >
      <path d="M16.37 1.43c0 1.14-.42 2.22-1.21 3.04-.84.88-2.1 1.55-3.27 1.46-.13-1.13.43-2.32 1.18-3.05.83-.81 2.23-1.42 3.3-1.45ZM20.5 17.05c-.55 1.27-.81 1.83-1.52 2.95-.99 1.55-2.39 3.49-4.13 3.5-1.55.02-1.95-1-4.05-1-2.1.01-2.54 1.02-4.09 1-1.74-.02-3.06-1.77-4.05-3.32C-.18 16.83-.5 11.66 1.84 8.93c1.18-1.39 3.05-2.27 4.85-2.27 1.83 0 2.98 1 4.49 1 1.46 0 2.36-1 4.47-1 1.59 0 3.27.87 4.47 2.36-3.93 2.16-3.29 7.78.38 8.03Z" />
    </svg>
  );
}

